import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import {
    FOLDER_NAME_MAX_LENGTH,
    FolderI,
    FolderPublicationReferenceI,
    hydrateFolderPublicationReferences
} from 'src/app/models/folder';
import { ProjectI } from 'src/app/models/project';
import { FolderService } from 'src/app/online/folder/service/folder.service';
import {
    FolderSortFieldE,
    PublicationSaveTypeE,
    SortDirectionT
} from '../../publication-save-in-folder-dialog/publication-save-in-folder-dialog.component';

@Component({
    selector: 'app-folder-selector',
    templateUrl: './folder-selector.component.html',
    styleUrls: ['./folder-selector.component.scss']
})
export class FolderSelectorComponent implements OnChanges {
    readonly FolderSortFieldE = FolderSortFieldE;
    readonly folderNameMaxLength = FOLDER_NAME_MAX_LENGTH;

    @Input() folders: FolderI[] = [];
    @Input() foldersLoading = false;
    // Hides the search/sort filter bar entirely - the folders are then just left sorted by name ascending
    // (the default sortField/sortDirection below), eg. for a short pinned-folders list where filtering/sorting
    // controls aren't worth the space.
    @Input() showFilter = true;
    // Only given when folders are browsed within a project of the current user - lets the user create new
    // folders inline. Left undefined for folders the user doesn't own a project for (eg. shared folders).
    @Input() project?: ProjectI;
    @Input() publicationId?: number;
    @Input() publicationType: PublicationSaveTypeE = PublicationSaveTypeE.DOCUMENT;
    @Input() selectedFolderId?: number;
    // The parent knows the up-to-date state of the publication (eg. a book's current bookParts,
    // or a document's current version) and is the only one able to tell whether a folder that
    // already contains the publication is holding a stale copy of it.
    @Input() isFolderOutdated?: (folder: FolderI) => boolean;
    @Input() updateOutdatedFolder?: (folder: FolderI) => Promise<void>;

    @Output() folderSelect = new EventEmitter<FolderI>();
    @Output() folderCreated = new EventEmitter<FolderI>();

    @ViewChild('newItemInput') private newItemInput: ElementRef<HTMLInputElement>;

    filteredFolders: FolderI[] = [];

    searchText = '';
    sortField: FolderSortFieldE = FolderSortFieldE.NAME;
    sortDirection: SortDirectionT = 'asc';

    updatingFolderIds = new Set<number>();

    creatingItem = false;
    newItemName = '';
    creatingItemLoading = false;

    constructor(private folderService: FolderService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.folders) {
            this.updateFilteredFolders();
        }
    }

    onSearchTextChange(searchText: string): void {
        this.searchText = searchText;
        this.updateFilteredFolders();
    }

    onSortFieldChange(sortField: FolderSortFieldE): void {
        this.sortField = sortField;
        this.updateFilteredFolders();
    }

    toggleSortDirection(): void {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        this.updateFilteredFolders();
    }

    selectFolder(folder: FolderI): void {
        if (this.isFolderAlreadyContainingPublication(folder)) {
            return;
        }
        this.folderSelect.emit(folder);
    }

    isFolderAlreadyContainingPublication(folder: FolderI): boolean {
        if (!this.publicationId) {
            return false;
        }
        const referencesByType: Record<PublicationSaveTypeE, FolderPublicationReferenceI[] | undefined> = {
            [PublicationSaveTypeE.DOCUMENT]: folder.documentReferences,
            [PublicationSaveTypeE.BOOK]: folder.bookPartReferences,
            [PublicationSaveTypeE.WHOLE_BOOK]: folder.bookReferences
        };
        return !!referencesByType[this.publicationType]?.some(reference => reference.id === this.publicationId);
    }

    isFolderUpdating(folder: FolderI): boolean {
        return this.updatingFolderIds.has(folder.id);
    }

    onUpdateOutdatedFolder(folder: FolderI, event: Event): void {
        event.stopPropagation();
        if (!this.updateOutdatedFolder || this.isFolderUpdating(folder)) {
            return;
        }
        this.updatingFolderIds.add(folder.id);
        this.updateOutdatedFolder(folder).finally(() => {
            this.updatingFolderIds.delete(folder.id);
        });
    }

    startCreatingItem(): void {
        this.creatingItem = true;
        this.newItemName = '';
        setTimeout(() => this.newItemInput?.nativeElement.focus());
    }

    cancelCreatingItem(): void {
        this.creatingItem = false;
        this.newItemName = '';
    }

    confirmCreateItem(): void {
        const name = this.newItemName?.trim();
        if (!name || this.creatingItemLoading || !this.project) {
            return;
        }
        this.creatingItemLoading = true;
        this.folderService.createFolderInProject(name, this.project.id).subscribe({
            next: folder => {
                const hydratedFolder = hydrateFolderPublicationReferences(folder);
                this.creatingItemLoading = false;
                this.creatingItem = false;
                this.folderCreated.emit(hydratedFolder);
                this.folderSelect.emit(hydratedFolder);
            },
            error: () => (this.creatingItemLoading = false)
        });
    }

    private updateFilteredFolders(): void {
        const searchText = this.searchText?.trim().toLowerCase() || '';
        const folders = (this.folders || []).filter(
            folder => !searchText || (folder.name || '').toLowerCase().includes(searchText)
        );
        this.filteredFolders = folders.sort((a, b) => this.compareItems(a.name, a.createdAt, b.name, b.createdAt));
    }

    private compareItems(
        nameA: string,
        createdAtA: string | undefined,
        nameB: string,
        createdAtB: string | undefined
    ): number {
        let comparison: number;
        if (this.sortField === FolderSortFieldE.NAME) {
            comparison = (nameA || '').localeCompare(nameB || '');
        } else {
            comparison = this.timestamp(createdAtA) - this.timestamp(createdAtB);
        }
        return this.sortDirection === 'asc' ? comparison : -comparison;
    }

    private timestamp(date?: string): number {
        return date ? new Date(date).getTime() : 0;
    }
}
